import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SpeedIcon from "@mui/icons-material/Speed";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useAuth } from "../context/AuthContext";
import style from "./landing.module.css";

interface DemoPair {
  left: { name: string; icon: string; elo: number };
  right: { name: string; icon: string; elo: number };
}

const DEMO_PAIRS: DemoPair[] = [
  {
    left: { name: "Wood-Fired Pizza", icon: "🍕", elo: 1420 },
    right: { name: "Smash Burger", icon: "🍔", elo: 1395 },
  },
  {
    left: { name: "Sci-Fi Movies", icon: "🌌", elo: 1510 },
    right: { name: "Fantasy Epics", icon: "🐉", elo: 1480 },
  },
  {
    left: { name: "Espresso Coffee", icon: "☕", elo: 1380 },
    right: { name: "Matcha Latte", icon: "🍵", elo: 1320 },
  },
];

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const [demoIndex, setDemoIndex] = useState(0);
  const [scores, setScores] = useState({
    left: DEMO_PAIRS[0].left.elo,
    right: DEMO_PAIRS[0].right.elo,
  });
  const [selectedSide, setSelectedSide] = useState<"left" | "right" | null>(null);

  const currentPair = DEMO_PAIRS[demoIndex];

  const handleDemoVote = (side: "left" | "right") => {
    setSelectedSide(side);
    if (side === "left") {
      setScores((prev) => ({ left: prev.left + 16, right: prev.right - 16 }));
    } else {
      setScores((prev) => ({ left: prev.left - 16, right: prev.right + 16 }));
    }

    setTimeout(() => {
      const nextIdx = (demoIndex + 1) % DEMO_PAIRS.length;
      setDemoIndex(nextIdx);
      setScores({
        left: DEMO_PAIRS[nextIdx].left.elo,
        right: DEMO_PAIRS[nextIdx].right.elo,
      });
      setSelectedSide(null);
    }, 900);
  };

  return (
    <div className={style.container}>
      {/* HERO SECTION */}
      <section className={style.heroSection}>
        <div className={style.badge}>
          <AutoAwesomeIcon className={style.badgeIcon} />
          <span>Rank Anything with 1v1 Battle Matchups</span>
        </div>

        <h1 className={style.heroTitle}>
          Turn Opinions into <br />
          <span className={style.gradientText}>Definitive Leaderboards</span>
        </h1>

        <p className={style.heroSubtitle}>
          Create custom lists, invite friends to vote in fast head-to-head battles, and let our
          ELO rating engine reveal true, unbiased rankings.
        </p>

        <div className={style.ctaGroup}>
          {isAuthenticated ? (
            <>
              <Button
                component={Link}
                to="/mashlist/wizard"
                variant="contained"
                className={style.primaryBtn}
                startIcon={<AddCircleOutlineIcon />}
              >
                Create a Mash-List
              </Button>
              <Button
                component={Link}
                to="/mashlist/list"
                variant="outlined"
                className={style.secondaryBtn}
                startIcon={<FormatListBulletedIcon />}
              >
                My Lists
              </Button>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                className={style.primaryBtn}
                endIcon={<ArrowForwardIcon />}
              >
                Get Started — Free
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                className={style.secondaryBtn}
              >
                Sign In
              </Button>
            </>
          )}
        </div>
      </section>

      {/* INTERACTIVE DEMO CARD */}
      <section className={style.demoSection}>
        <div className={style.demoCard}>
          <div className={style.demoHeader}>
            <span className={style.demoTag}>
              <SpeedIcon style={{ fontSize: "1.2rem", marginRight: "6px" }} /> Interactive Demo
            </span>
            <p className={style.demoInstruction}>Which one is better? Click to vote!</p>
          </div>

          <div className={style.matchupContainer}>
            <div
              className={`${style.contenderCard} ${
                selectedSide === "left" ? style.winnerCard : ""
              }`}
              onClick={() => handleDemoVote("left")}
            >
              <span className={style.contenderIcon}>{currentPair.left.icon}</span>
              <h3 className={style.contenderName}>{currentPair.left.name}</h3>
              <span className={style.eloBadge}>Rating: {scores.left}</span>
              {selectedSide === "left" && <span className={style.winnerBadge}>+16 ELO Winner!</span>}
            </div>

            <div className={style.vsCircle}>
              <span>VS</span>
            </div>

            <div
              className={`${style.contenderCard} ${
                selectedSide === "right" ? style.winnerCard : ""
              }`}
              onClick={() => handleDemoVote("right")}
            >
              <span className={style.contenderIcon}>{currentPair.right.icon}</span>
              <h3 className={style.contenderName}>{currentPair.right.name}</h3>
              <span className={style.eloBadge}>Rating: {scores.right}</span>
              {selectedSide === "right" && <span className={style.winnerBadge}>+16 ELO Winner!</span>}
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA BANNER */}
      <section className={style.bottomCtaSection}>
        <div className={style.bottomCtaCard}>
          <h2>Ready to settle the debate?</h2>
          <p>Create your custom Mash-List and start ranking today.</p>
          <Button
            component={Link}
            to={isAuthenticated ? "/mashlist/wizard" : "/register"}
            variant="contained"
            className={style.bottomCtaBtn}
            endIcon={<ArrowForwardIcon />}
          >
            {isAuthenticated ? "Create a Mash-List Now" : "Create Free Account"}
          </Button>
        </div>
      </section>
    </div>
  );
}
